/**
 * CartItem — pure UI component for a single cart flyout line item.
 * Layout:
 *   [80×80 image] [content flex-1] [price column 45px]
 */
import {useCallback, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import {productUrlBuilder} from '@salesforce/retail-react-app/app/utils/url';

import {filterImageGroups} from '@salesforce/retail-react-app/app/utils/product-utils';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  Flex,
  Spinner,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import QuantityPicker from '@amblique/king-living/app/components/quantity-picker';
import {AlertErrorIcon} from '@amblique/king-living/app/components/icons';
import {
  formatLineItemOptionRow,
  getLineItemOptionRows,
  getMaxQuantityForGroup,
  resolveBasketLineMerchandisingCopy,
} from '@amblique/king-living/app/utils/cart-utils';
import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';
import {
  CYLINDO_SIZE,
  normalizePlainTileImageSrc,
} from '@amblique/king-living/app/utils/responsive-image-src';

const CartItem = ({
  currency,
  isOutOfStock = false,
  isEditDisabled = false,
  isRemoving,
  isUpdating,
  lineItem,
  onChangeQuantity,
  onEdit,
  onRemove,
  outOfStockAlertLayout = 'flyout',
  productsByItemId,
  productsByProductId,
  promotionsById,
}) => {
  const intl = useIntl();
  const styles = useMultiStyleConfig('CartFlyout', {});
  const isFlyoutAlertLayout = outOfStockAlertLayout === 'flyout';
  const inlineAlertDisplay = isFlyoutAlertLayout ?
    {base: 'block', lg: 'block'} :
    {base: 'none', lg: 'block'};
  const bottomAlertDisplay = isFlyoutAlertLayout ?
    {base: 'none', lg: 'none'} :
    {base: 'block', lg: 'none'};
  const product = productsByItemId?.[lineItem.itemId];

  const {name: productName, shortDescription} = useMemo(
    () => resolveBasketLineMerchandisingCopy(lineItem, productsByProductId, product),
    [lineItem, product, productsByProductId]
  );

  // Parsed configuration details from c_selectedOptionsUI
  const {configOptions, coverName} = useMemo(() => {
    if (!lineItem.c_selectedOptionsUI) return {configOptions: [], coverName: null};

    try {
      const parsed = JSON.parse(lineItem.c_selectedOptionsUI);

      return {
        configOptions: parsed.options ?? [],
        coverName: parsed.coverName ?? null,
      };
    } catch {
      return {configOptions: [], coverName: null};
    }
  }, [lineItem.c_selectedOptionsUI]);

  // SCAPI Product Options (e.g. KingGuard) attached to the basket line. Only
  // priced ("Yes") values surface; the "none" value is filtered out so the
  // shopper sees a row exclusively when they opted in.
  const productOptionRows = useMemo(
    () => getLineItemOptionRows(lineItem, product),
    [lineItem, product]
  );

  // Thumbnail URL resolution:
  //   1. Complex parent items: read cylindoUrl pre-computed by
  //      resolveCommittedSelectionHeroVisualUrl() at add-to-cart time and stored
  //      in c_selectedOptionsUI. This is the configured PIM/Cylindo image.
  //   2. Standard products: SFCC imageGroups (prefer 'large', fall back to first group).
  const imageUrl = useMemo(() => {
    // 1. Complex parent — cylindoUrl stored at add-to-cart time
    if (lineItem.c_selectedOptionsUI) {
      try {
        const parsed = JSON.parse(lineItem.c_selectedOptionsUI);

        if (parsed?.cylindoUrl) {
          return normalizePlainTileImageSrc(parsed.cylindoUrl, {cylindoSize: CYLINDO_SIZE.thumb});
        }
      } catch { /* ignore malformed JSON */ }
    }

    // 2. Standard SFCC imageGroups
    const groups = product?.imageGroups ?? lineItem.imageGroups ?? [];
    const filtered = filterImageGroups(groups, {viewType: 'large'});
    const group = filtered[0] ?? groups[0];

    return normalizePlainTileImageSrc(group?.images?.[0]?.link ?? '', {
      cylindoSize: CYLINDO_SIZE.thumb,
    });
  }, [lineItem, product]);

  const imageAlt = productName;

  // Maximum orderable quantity — min ATS across parent + all child products
  const maxQty = useMemo(
    () => getMaxQuantityForGroup(lineItem, productsByItemId ?? {}),
    [lineItem, productsByItemId]
  );

  const currentQty = lineItem.quantity;
  const isAtMax = typeof maxQty === 'number' && isFinite(maxQty) && currentQty >= maxQty;

  // Local flag set when user manually types a value exceeding ATS.
  // When true, the API call is suppressed and the error alert is shown immediately.
  const [isOverMax, setIsOverMax] = useState(false);

  const showMaxError = !isOutOfStock && (isAtMax || isOverMax);

  // QuantityPicker.onChange fires on every keystroke.
  // If the typed value exceeds ATS: show error immediately, skip the API call.
  // NOTE: Currently unused because QuantityPicker is disabled (KL-930).
  // Keep this logic ready for when quantity editing is unblocked.
  const handleQuantityChange = useCallback(
    (value) => {
      const qty = parseInt(value, 10);

      if (isNaN(qty)) return;

      if (isFinite(maxQty) && qty > maxQty) {
        setIsOverMax(true);

        return;
      }

      setIsOverMax(false);

      if (qty !== currentQty) {
        onChangeQuantity(lineItem, qty);
      }
    },
    [currentQty, lineItem, maxQty, onChangeQuantity]
  );

  const handleRemove = useCallback(() => onRemove(lineItem), [lineItem, onRemove]);

  // Resolve edit URL via the centralised builder. The line item carries the slug captured at
  // add-to-cart time (`lineItem.c_productPageURL`); if it's missing we fall through to the
  // catalog product (which may expose its own `c_productPageURL`, or otherwise just `id`).
  // The king-living `productUrlBuilder` override handles the `c_productPageURL` → `/{slug}`
  // conversion and the `/product/{id}` fallback in one place.
  const editUrl = productUrlBuilder(lineItem.c_productPageURL ? lineItem : (product ?? lineItem));

  // Edit: pass the resolved URL so the parent can navigate without re-deriving it
  const handleEdit = useCallback(() => onEdit(editUrl), [onEdit, editUrl]);

  const promoCallouts = useMemo(() => {
    const seen = new Set();

    return (lineItem.priceAdjustments ?? [])
      .filter((adj) => {
        if (!adj.promotionId || seen.has(adj.promotionId)) return false;
        seen.add(adj.promotionId);

        return true;
      })
      .map((adj) => {
        const promo = promotionsById?.[adj.promotionId];
        const msg = promo?.calloutMsg;

        // Skip raw promotion IDs when callout copy is not configured in BM.
        if (!msg || msg === adj.promotionId) return null;

        return msg;
      })
      .filter(Boolean);
  }, [lineItem.priceAdjustments, promotionsById]);

  // Price display:
  // - Complex parent items: basePrice = configuredListTotal (RRP, set in groupComplexLineItems).
  //   Used as the strikethrough "original" price to match the PDP.
  //   salePrice = priceAfterItemDiscount (sfccAfterItemDiscount) to match basket.productSubTotal.
  // - Standard items: unitPrice = SFCC list price, salePrice = price after item discount.
  const isComplexParent = Boolean(lineItem._isComplexParent);
  const unitPrice = isComplexParent ?
    (lineItem.basePrice ?? lineItem.price ?? 0) :
    (lineItem.price ?? 0);
  const salePrice = lineItem.priceAfterItemDiscount ?? lineItem.price ?? 0;
  const hasDiscount = unitPrice > salePrice;

  const formatCurrency = useCallback(
    (amount) => formatCurrencyHideTrailingZeroCents(intl, amount, currency),
    [intl, currency]
  );

  const isBusy = isRemoving || isUpdating;

  const renderOutOfStockAlert = () => (isOutOfStock ? (
    <Alert
      className="cart-item__out-of-stock-alert"
      status="error"
      variant="error"
      w="full"
      {...styles.itemOutOfStockAlert}
      {...(isFlyoutAlertLayout ? styles.itemOutOfStockAlertFlyout : {})}
    >
      <AlertIcon as={AlertErrorIcon} />
      <AlertTitle {...styles.itemOutOfStockAlertText}>
        {intl.formatMessage({
          defaultMessage:
            'This item is out of stock. Please remove it from your cart to continue.',
          id: 'checkout_order_summary.line_item.out_of_stock',
        })}
      </AlertTitle>
    </Alert>
  ) : null);

  return (
    // aria-busy signals to screen readers that a mutation is in-flight
    <><Flex
      aria-busy={isBusy ? 'true' : undefined}
      className="cart-item__root"
      {...styles.itemRoot}
    >
      {/* Thumbnail — 80×80 */}
      <Box className="cart-item__thumbnail" {...styles.itemThumbnail}>
        {imageUrl && (
          <Box
            alt={imageAlt}
            as="img"
            className="cart-item__thumbnail-img"
            display="block"
            h="100%"
            loading="lazy"
            objectFit="contain"
            src={imageUrl}
            w="100%"
          />
        )}
      </Box>

      {/* Content column */}
      <Flex className="cart-item__content" {...styles.itemContent}>
        <Text className="cart-item__name" noOfLines={2} {...styles.itemName}>
          {productName}
        </Text>

        {shortDescription && (
          <Text className="cart-item__short-desc" noOfLines={2} {...styles.itemShortDesc}>
            {shortDescription}
          </Text>
        )}

        {(coverName ||
          configOptions.some((opt) => !opt.isHidden) ||
          productOptionRows.length > 0) && (
          <Box className="cart-item__config-group" {...styles.itemConfigGroup}>
            {coverName && (
              <Text className="cart-item__config-text" {...styles.itemConfigText}>
                Cover: {coverName}
              </Text>
            )}

            {configOptions.filter((opt) => !opt.isHidden).map((opt, idx) => (
              <Text key={idx} className="cart-item__config-text" {...styles.itemConfigText}>
                {opt.name}: {opt.value}
              </Text>
            ))}

            {productOptionRows.map((row) => (
              <Text
                key={`opt-${row.optionId}`}
                className="cart-item__config-text"
                {...styles.itemConfigText}
              >
                {formatLineItemOptionRow(intl, row)}
              </Text>
            ))}
          </Box>
        )}
        {promoCallouts.length > 0 && promoCallouts.map((msg, idx) => (
          <Text key={idx} className="cart-item__promo" {...styles.itemPromoText}>
            {msg}
          </Text>
        ))}

        {/* Bottom row: quantity stepper + Edit / Remove */}
        <Flex className="cart-item__actions" {...styles.itemActions}>
          {/* QuantityPicker — disabled (KL-930); remove isDisabled/isReadOnly to re-enable */}
          <Box className="cart-item__qty-wrapper" {...styles.itemQuantity}>
            <QuantityPicker
              max={isFinite(maxQty) ? maxQty : undefined}
              min={1}
              productName={productName}
              value={currentQty}
              isDisabled
              isReadOnly
              variant={isFlyoutAlertLayout ? 'cartFlyout' : isEditDisabled ? 'checkout' : 'default'}
              onChange={handleQuantityChange}
            />
          </Box>

          {isFlyoutAlertLayout && (
            <Box
              className="out-of-stock-alert-container out-of-stock-alert-container__inline out-of-stock-alert-container__inline--flyout-mobile"
              display={{base: inlineAlertDisplay.base, lg: 'none'}}
              flexBasis={{base: '100%', lg: 'auto'}}
              marginBottom="0"
              marginTop="0"
              width={{base: '100%', lg: 'auto'}}
            >
              {renderOutOfStockAlert()}
            </Box>
          )}

          {/* Edit | Remove CTAs — separated by a thin vertical keyline */}
          <Flex align="center" className="cart-item__cta-group" {...styles.itemCtaGroup}>
            {editUrl && (
              <>
                <Box
                  as="button"
                  className="cart-item__edit-btn"
                  type="button"
                  aria-disabled={isEditDisabled ? 'true' : undefined}
                  disabled={isEditDisabled}
                  aria-label={intl.formatMessage(
                    {defaultMessage: 'Edit {name}', id: 'cart_item.edit.aria_label'},
                    {name: productName}
                  )}
                  {...styles.itemCta}
                  {...(isEditDisabled ? styles.itemCtaDisabled : {})}
                  onClick={isEditDisabled ? undefined : handleEdit}
                >
                  {intl.formatMessage({defaultMessage: 'Edit', id: 'cart_item.edit.label'})}
                </Box>
                <Box className="cart-item__cta-sep" {...styles.itemCtaSeparator} />
              </>
            )}
            <Box
              as="button"
              className="cart-item__remove-btn"
              type="button"
              aria-label={intl.formatMessage(
                {defaultMessage: 'Remove {name}', id: 'cart_item.remove.aria_label'},
                {name: productName}
              )}
              {...styles.itemRemoveCta}
              onClick={handleRemove}
            >
              {intl.formatMessage({defaultMessage: 'Remove', id: 'cart_item.remove.label'})}
            </Box>
          </Flex>
        </Flex>
        <Box
          className="out-of-stock-alert-container out-of-stock-alert-container__inline"
          display={
            isFlyoutAlertLayout ?
              {base: 'none', lg: inlineAlertDisplay.lg} :
              inlineAlertDisplay
          }
        >
          {renderOutOfStockAlert()}
        </Box>

        {/* Max-quantity error alert — shown when manual input or stepper exceeds ATS */}
        {showMaxError && (
          <Flex
            align="flex-start"
            className="cart-item__qty-max-error"
            gap={2}
            {...styles.qtyMaxError}
          >
            <AlertErrorIcon boxSize="20px" flexShrink={0} />
            <Text className="cart-item__qty-max-error-text" {...styles.qtyMaxErrorText}>
              {intl.formatMessage({
                defaultMessage: 'Maximum quantity selected.',
                id: 'cart_item.qty.max_warning',
              })}
            </Text>
          </Flex>
        )}
      </Flex>

      {/* Price column */}
      <Flex className="cart-item__price-col" {...styles.itemPriceCol}>
        <Text className="cart-item__price-current" {...styles.itemCurrentPrice}>
          {formatCurrency(salePrice)}
        </Text>
        {hasDiscount && (
          <Text className="cart-item__price-original" {...styles.itemOriginalPrice}>
            {formatCurrency(unitPrice)}
          </Text>
        )}
      </Flex>

      {isBusy && (
        <Box
          aria-hidden="true"
          bg={isRemoving ? 'whiteAlpha.700' : 'whiteAlpha.500'}
          className="cart-item__loading-overlay"
          {...styles.itemLoadingOverlay}
        >
          <Spinner
            color="greyscale.100"
            size={isRemoving ? 'md' : 'sm'}
            thickness="2px"
          />
        </Box>
      )}
    </Flex>
    <Box
      className="out-of-stock-alert-container out-of-stock-alert-container__bottom"
      display={bottomAlertDisplay}
    >
      {renderOutOfStockAlert()}
    </Box></>
  );
};

CartItem.displayName = 'CartItem';
CartItem.propTypes = {
  currency: PropTypes.string.isRequired,
  isOutOfStock: PropTypes.bool,
  isEditDisabled: PropTypes.bool,
  outOfStockAlertLayout: PropTypes.oneOf(['checkout', 'flyout']),
  isRemoving: PropTypes.bool,
  isUpdating: PropTypes.bool,
  lineItem: PropTypes.object.isRequired,
  onChangeQuantity: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  productsByItemId: PropTypes.object.isRequired,
  productsByProductId: PropTypes.object,
  promotionsById: PropTypes.object,
};

export default CartItem;
